import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AppService, GetDentalBidirectionalListsRequest, DentalBidirectionalListItem, DentalReferralForm, GetReferralByReferralIdRequest, GetReferralByReferralIdResponse, SaveDentalReferralReviewRequest } from '../../app.service';
import { LoadingService } from '../loading.service';
import { AlertService } from '../alert.service';
import { catchError, finalize, of, tap } from 'rxjs';

export type SourceType = 'PCP' | 'HRAT' | 'Maternity';

export interface ReferralStatusDialogData {
  sourceType: SourceType;
  referralId: number;
  isClosed: boolean;
  currentDcoNote?: string | null;
}

export interface ReferralStatusResult {
  saved: boolean;
}

@Component({
  selector: 'app-referral-status-dialog',
  templateUrl: './referral-status-dialog.component.html',
  styleUrls: ['./referral-status-dialog.component.scss']
})
export class ReferralStatusDialogComponent implements OnInit {
  form!: FormGroup;
  isReadOnly = false;
  referral?: DentalReferralForm;
  loadingReferral = false;
  dcoNotes: DentalBidirectionalListItem[] = [];
  loadingNotes = false;
  saving = false;

  constructor(
    private fb: FormBuilder,
    private service: AppService,
    //private spinner: LoadingService,
    private alert: AlertService,
    private dialogRef: MatDialogRef<ReferralStatusDialogComponent, ReferralStatusResult>,
    @Inject(MAT_DIALOG_DATA) public data: ReferralStatusDialogData
  ) { }

  ngOnInit(): void {
    this.form = this.fb.group({
      // user must confirm they intend to close
      closeReferral: [true, Validators.requiredTrue],
      // required: we want a DCO Note when closing
      dcoNote: [this.data.currentDcoNote ?? null, Validators.required],
    });

    this.loadReferralDetails();
    this.loadDcoNotes();
  }

  private loadReferralDetails() {
    this.loadingReferral = true;
    //this.spinner.show();

    const req: GetReferralByReferralIdRequest = {
      referralId: this.data.referralId,
      referralType: this.data.sourceType
    };

    this.service.GetReferralByReferralId(req).pipe(
      tap(res => {
        if (res?.success && res.referral) {
          this.referral = res.referral;

          // ✅ If it's already closed, mark dialog as read-only
          this.isReadOnly = !!this.referral.review?.isClosed;

          if (this.isReadOnly) {
            this.form.disable();  // disable all controls
          } else {
            const note = this.referral.review?.dcoNote ?? this.data.currentDcoNote;
            if (note) this.form.get('dcoNote')?.setValue(note);
          }
        } else {
          this.alert.warning(res?.message || 'Unable to retrieve referral details.');
        }
      }),
      catchError(err => {
        console.error(err);
        this.alert.error('Error fetching referral details.');
        return of(null);
      }),
      finalize(() => {
        this.loadingReferral = false;
        //this.spinner.hide();
      })
    ).subscribe();
  }

  private loadDcoNotes() {
    this.loadingNotes = true;
    //this.spinner.show();

    const req = new GetDentalBidirectionalListsRequest();
    this.service.GetDentalBidirectionalLists(req).pipe(
      tap(res => {
        if (res?.success) {
          const list = res.lists.find(l => l.name === 'DCONotes');
          this.dcoNotes = (list?.listItems ?? []).filter(i => i.active);

          // Only set a default if the form doesn't already have a value
          this.tryDefaultDcoNote();
        } else {
          this.alert.error(res?.message || 'Could not load DCO Notes.');
        }
      }),
      catchError(err => {
        console.error(err);
        this.alert.error('Error loading DCO Notes.');
        return of(null);
      }),
      finalize(() => {
        this.loadingNotes = false;
        //this.spinner.hide();
      })
    ).subscribe();
  }

  cancel() {
    this.dialogRef.close({ saved: false });
  }

  save() {

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { dcoNote, closeReferral } = this.form.value as { dcoNote: string; closeReferral: boolean };

    // If you want to combine dropdown + free text:
    const finalNote = dcoNote;

    this.saving = true;

    //this.spinner.show();
    const request = new SaveDentalReferralReviewRequest();

    this.referral.review.isClosed = closeReferral;
    this.referral.review.dcoNote = dcoNote;

    request.review = this.referral.review;
    request.review.referralId = this.referral.id;
    request.review.referralType = this.referral.referralType;

    this.service.SaveDentalReferralReview(request)
      .pipe(
        finalize(() => {
          this.saving = false;
          //this.spinner.hide();
        })
      )
      .subscribe({
        next: (res: any) => {
          if (res?.success) {
            this.dialogRef.close({ saved: true });
          } else {
            this.alert.error(res?.message || 'Failed to update referral.');
          }
        },
        error: err => {
          console.error(err);
          this.alert.error('Unexpected error updating referral.');
        }
      });
  }

  private tryDefaultDcoNote() {
    const ctrl = this.form.get('dcoNote');
    if (!ctrl) return;

    // If a value is already set (from currentDcoNote or review.dcoNote), do nothing
    const existing = ctrl.value;
    if (existing && String(existing).trim().length > 0) return;

    // Look for “Duplicate” by value or display text (case-insensitive)
    const dup = this.dcoNotes.find(i => {
      const v = (i.name ?? '').toString().trim().toLowerCase();
      const d = (i.name ?? '').toString().trim().toLowerCase();
      return v === 'duplicate' || d === 'duplicate';
    })
    // optionally relax match to contains:
    // || this.dcoNotes.find(i => `${i.value ?? ''} ${i.displayText ?? ''}`.toLowerCase().includes('duplicate'));

    if (dup) {
      ctrl.setValue(dup.name);
    }
  }
}
